import { useCallback, useEffect, useState } from "react";
import { Link, Outlet, useParams } from "react-router";
import { ApiError, api } from "@/lib/api";
import { findModule, brandHasTool } from "@/lib/modules";
import { useAccountWorkspace } from "@/lib/account-workspace";
import { Button } from "@/components/ui/button";

type WecomReady = { ready: boolean };

/** 客户旅程四栏壳：品牌未开通则挡；未就绪条幅 + 子路由 Outlet */
export default function JourneyLayout() {
  const { brandId } = useParams();
  const { accountId, brands, modules, isLoading } = useAccountWorkspace();
  const brand = brands.find((item) => String(item.id) === brandId);
  const app = findModule(modules, "journey");
  const brandToolEnabled = brandHasTool(brand, "journey");
  const [ready, setReady] = useState<boolean | null>(null);

  const loadReady = useCallback(async () => {
    try {
      const payload = await api.fetchForAccount<WecomReady>(accountId, "/account/wecom_connection");
      setReady(payload.ready);
    } catch (caught) {
      // 读取失败时不挡浏览；视为未就绪以保守禁用启用
      console.warn("无法加载企微就绪态", caught instanceof ApiError ? caught.message : caught);
      setReady(false);
    }
  }, [accountId]);

  useEffect(() => {
    if (!brandToolEnabled) return;
    void loadReady();
  }, [brandToolEnabled, loadReady]);

  if (isLoading) {
    return <p className="text-sm text-muted-foreground">加载中…</p>;
  }

  if (!app || !app.enabled) {
    return (
      <div className="space-y-3">
        <p className="text-sm text-muted-foreground">客户旅程未订阅或不存在。</p>
        {brandId ? (
          <Link
            to={`/accounts/${accountId}/brands/${brandId}`}
            className="text-sm text-primary-readable hover:underline"
          >
            返回品牌
          </Link>
        ) : null}
      </div>
    );
  }

  if (!brand) {
    return (
      <div className="space-y-3">
        <p className="text-sm text-muted-foreground">未找到该品牌。</p>
        <Link to={`/accounts/${accountId}`} className="text-sm text-primary-readable hover:underline">
          返回企业画像
        </Link>
      </div>
    );
  }

  if (!brandToolEnabled) {
    return (
      <div className="rounded-lg border border-dashed border-border bg-card px-6 py-10 text-center">
        <p className="text-sm font-medium text-foreground">本品牌尚未开通客户旅程</p>
        <p className="mt-1 text-xs text-muted-foreground">
          「{brand.name}」需先在品牌面板开通工具，才能进入总览、客户与旅程设置。
        </p>
        <Button asChild className="mt-4" size="sm">
          <Link to={`/accounts/${accountId}/brands/${brandId}`}>前往品牌开通</Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {ready === false ? (
        <div
          className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-900 dark:text-amber-200"
          role="status"
        >
          <span>请先完成企微接入后才能启用旅程</span>
          <Link
            to={`/accounts/${accountId}/wecom`}
            className="font-medium text-primary underline-offset-4 hover:underline"
          >
            去企微接入
          </Link>
        </div>
      ) : null}

      <Outlet context={{ ready: ready === true, brandName: brand.name, refreshReady: loadReady }} />
    </div>
  );
}

export type JourneyOutletContext = {
  ready: boolean;
  brandName: string | null;
  refreshReady: () => Promise<void>;
};
